import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Printer } from 'lucide-react';
import { notesApi } from '@/lib/supabase';
import { useLocalizedContent } from '@/hooks/useLocalizedContent';
import CitationButton from '@/components/CitationButton';
import DOMPurify from 'dompurify';
import { optimizeImageUrl } from '@/lib/imageOptimize';

// Configure DOMPurify to allow iframes from trusted sources
if (typeof window !== 'undefined') {
  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName === 'iframe') {
      const src = node.getAttribute('src') || '';
      const trustedDomains = ['youtube.com', 'youtube-nocookie.com', 'youtu.be', 'vimeo.com', 'player.vimeo.com', 'google.com/maps', 'maps.google.com', 'open.spotify.com', 'soundcloud.com', 'w.soundcloud.com', 'bandcamp.com', 'archive.org', 'drive.google.com', 'docs.google.com', 'calendly.com', 'player.ausha.co', 'ausha.co', 'anchor.fm', 'podcasters.spotify.com', 'embed.podcasts.apple.com', 'www.buzzsprout.com', 'share.transistor.fm', 'player.simplecast.com', 'embed.acast.com'];
      const isTrusted = trustedDomains.some(domain => src.includes(domain));
      if (!isTrusted && src) {
        node.remove();
      }
    }
  });
}

const SANITIZE_CONFIG = {
  ADD_TAGS: ['iframe'],
  ADD_ATTR: ['allowfullscreen', 'frameborder', 'allow', 'src', 'width', 'height', 'style', 'title', 'class', 'loading', 'scrolling'],
  ALLOW_UNKNOWN_PROTOCOLS: false
};
const LegacyBlockContent = ({ content }) => {
  if (!content || !Array.isArray(content)) return null;
  
  return (
    <div className="space-y-8">
      {content.map((block, index) => {
        if (block.type === 'text') {
          return (
            <motion.p
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 + (index * 0.05) }}
              className="font-sans text-lg leading-relaxed text-[#1C1C1C]"
            >
              {block.content}
            </motion.p>
          );
        }
        
        if (block.type === 'quote') {
          if (block.featured) {
            return (
              <motion.blockquote
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 + (index * 0.05) }}
                className="group my-12 py-8 px-6 md:px-12 bg-[#F5F2EB] border-l-4 border-[#E5E0D6] hover:border-[#8B6F47] transition-all duration-300"
              >
                <p className="font-cutive font-bold text-xl md:text-2xl lg:text-3xl italic leading-relaxed text-[#1C1C1C] group-hover:text-[#8B6F47] transition-colors">
                  {block.content}
                </p>
              </motion.blockquote>
            );
          }
          return (
            <motion.blockquote
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 + (index * 0.05) }}
              className="group my-8 py-6 px-6 md:px-8 bg-[#F5F2EB] border-l-4 border-[#E5E0D6]"
            >
              <p className="font-cutive text-lg md:text-xl italic leading-relaxed text-[#1C1C1C]">
                {block.content}
              </p>
            </motion.blockquote>
          );
        }
        
        if (block.type === 'image') {
          return (
            <motion.figure
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 + (index * 0.05) }}
              className="my-12"
            >
              <div className="aspect-[16/9] overflow-hidden border border-[#E5E0D6]">
                <img 
                  src={block.src} 
                  alt={block.caption}
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              {(block.caption || block.credit) && (
                <figcaption className="caption text-muted-foreground mt-4">
                  {block.caption && <div>{block.caption}</div>}
                  {block.credit && <div className="text-[10px] mt-1">Credit: {block.credit}</div>}
                </figcaption>
              )}
            </motion.figure>
          );
        }
        
        return null;
      })}
    </div>
  );
};

const NotePage = () => {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const { getField, isArabic } = useLocalizedContent();

  useEffect(() => {
    const fetchNote = async () => {
      try {
        const data = await notesApi.getBySlug(slug);
        setNote(data);
      } catch (err) {
        console.error('Error fetching note:', err);
        setError('Note not found');
      } finally {
        setLoading(false);
      }
    };
    fetchNote();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] px-6 md:px-12 lg:px-24 py-24 flex items-center justify-center">
        <div className="text-center">
          <h1 className="heading-large mb-4">{isArabic ? 'الملاحظة غير موجودة' : 'Note not found'}</h1>
          <Link to="/notes" className="caption link-subtle">
            {isArabic ? 'العودة إلى الديوان' : 'Return to Reading Room'}
          </Link>
        </div>
      </div>
    );
  }

  // Get localized content
  const noteTitle = getField(note, 'title');
  const htmlContent = getField(note, 'content_html');

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Back Navigation */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="px-6 md:px-12 lg:px-24 pt-24 pb-12"
      >
        <Link 
          to="/notes" 
          className="inline-flex items-center gap-2 caption text-muted-foreground hover:text-[#8B6F47] transition-colors"
        >
          <ArrowLeft size={14} className={isArabic ? 'rotate-180' : ''} /> {isArabic ? 'العودة إلى الديوان' : 'Back to Reading Room'}
        </Link>
      </motion.div>

      {/* Featured Image */}
      {note.image && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="px-6 md:px-12 lg:px-24 mb-12"
        >
          <div className="max-w-4xl mx-auto aspect-[16/9] overflow-hidden border border-[#E5E0D6]">
            <img 
              src={optimizeImageUrl(note.image, 1200)} 
              alt={noteTitle}
              width="1200"
              height="675"
              className="w-full h-full object-cover grayscale"
            />
          </div>
        </motion.div>
      )}

      {/* Article Content */}
      <article className="px-6 md:px-12 lg:px-24 pb-24">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="caption text-muted-foreground">
                {note.date}{note.author && ` · ${isArabic ? t('notes.by') : 'By'} ${note.author}`}
              </span>
              <div className="flex items-center gap-4" style={{ lineHeight: 1 }}>
                <button
                  onClick={() => window.print()}
                  className="caption link-subtle text-muted-foreground inline-flex items-center gap-1.5"
                  data-testid="print-button"
                  aria-label={isArabic ? 'طباعة' : 'Print'}
                >
                  <Printer size={13} />
                  {isArabic ? 'طباعة' : 'Print'}
                </button>
                <CitationButton note={note} isArabic={isArabic} />
              </div>
            </div>
            
            <h1 className="heading-large mb-12" data-testid="note-title">
              {noteTitle}
            </h1>
          </motion.div>

          {/* Note Content - Supports both TipTap HTML and legacy JSON blocks */}
          {htmlContent ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="note-content prose prose-lg max-w-none"
              data-testid="note-content"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(htmlContent, SANITIZE_CONFIG)
              }}
            />
          ) : (
            <LegacyBlockContent content={note.content} />
          )}

          {/* References Section - English font since most references are in English */}
          {note.reference_list && note.reference_list.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-16 pt-8 border-t border-[#E5E0D6]"
              dir="ltr"
              style={{ textAlign: 'left' }}
            >
              <h2 className="caption text-muted-foreground mb-6" style={{ fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.2em', textTransform: 'uppercase', fontSize: '0.75rem' }}>
                {t('notes.references')}
              </h2>
              <ul className="space-y-3">
                {note.reference_list.map((ref, index) => (
                  <li key={index} className="text-base leading-relaxed text-[#1C1C1C]" style={{ fontFamily: "'Century Gothic Paneuropean', 'Century Gothic', 'CenturyGothic', 'Questrial', -apple-system, sans-serif" }}>
                    {ref}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          {/* Footer Navigation */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.2 }}
            className="mt-16 pt-8 border-t border-[#E5E0D6]"
          >
            <Link 
              to="/notes" 
              className="caption link-subtle inline-flex items-center gap-2"
            >
              <ArrowLeft size={14} className={isArabic ? 'rotate-180' : ''} /> {isArabic ? 'العودة إلى الديوان' : 'Return to Reading Room'}
            </Link>
          </motion.div>
        </div>
      </article>
    </div>
  );
};

export default NotePage;
